import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { usePecStore } from '../store'

/**
 * @fileoverview PEC Launch Tab
 *
 * Launch cycles and manage cycle status
 */

export const PecLaunchTab = () => {
	const { pecId } = useParams<{ pecId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId
	const { pecCycle, useMarkReadyPecCycle, useLaunchPecCycle, useClosePecCycle } = usePecStore()
	const { markReadyPecCycle, isLoading: isMarkingReady } = useMarkReadyPecCycle(pecId as string)
	const { launchPecCycle, isLoading: isLaunching } = useLaunchPecCycle(pecId as string)
	const { closePecCycle, isLoading: isClosing } = useClosePecCycle(pecId as string)
	const toast = useToast()
	const queryClient = useQueryClient()

	// Fetch population count when a population is selected
	const { data: populationCount } = useQuery({
		...trpc.client.pec.getPopulationCount.queryOptions({
			organizationId: organizationId as string,
			populationId: pecCycle?.populationId ?? '',
		}),
		enabled: !!organizationId && !!pecCycle?.populationId,
	})

	// Fetch cycle statistics when cycle is active or closed
	const { data: statistics } = useQuery({
		...trpc.client.pec.getStatistics.queryOptions({
			id: pecId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId && !!pecId && (pecCycle?.status === 'active' || pecCycle?.status === 'closed'),
		refetchInterval: pecCycle?.status === 'active' ? 30000 : false,
	})

	const hasEnoughParticipants = populationCount !== undefined && populationCount >= 2

	const handleMarkReady = async () => {
		if (!pecCycle || !organizationId || !pecId) return

		if (!pecCycle.populationId) {
			toast.showToast('Please configure a population first', 'error')
			return
		}

		if (!hasEnoughParticipants) {
			toast.showToast('PEC cycles require at least 2 participants', 'error')
			return
		}

		if (!pecCycle.promptText) {
			toast.showToast('Please configure prompt text first', 'error')
			return
		}

		await markReadyPecCycle()
		queryClient.invalidateQueries({
			queryKey: ['pec'],
		})
	}

	const handleLaunch = async () => {
		if (!pecCycle || !organizationId || !pecId) return

		if (pecCycle.status !== 'ready') {
			toast.showToast('Cycle must be ready before launching', 'error')
			return
		}

		await launchPecCycle()
		queryClient.invalidateQueries({
			queryKey: ['pec'],
		})
	}

	const handleClose = async () => {
		if (!pecCycle || !organizationId || !pecId) return

		if (pecCycle.status !== 'active') {
			toast.showToast('Only active cycles can be closed', 'error')
			return
		}

		await closePecCycle()
		queryClient.invalidateQueries({
			queryKey: ['pec'],
		})
	}

	const isPending = isMarkingReady || isLaunching || isClosing

	const formatStatus = (status: string) => {
		return status.charAt(0).toUpperCase() + status.slice(1)
	}

	return (
		<div className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='px-6 max-w-4xl space-y-6'>
				<div>
					<Text variant='large' weight='semibold'>
						Cycle Status
					</Text>
					<div className='mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4'>
						<div className='flex items-center justify-between'>
							<div>
								<Text variant='small' weight='semibold'>
									Current Status
								</Text>
								<Text variant='small' className='text-neutral-600 mt-1'>
									{pecCycle?.status ? formatStatus(pecCycle.status) : 'Unknown'}
								</Text>
							</div>
						</div>
					</div>
				</div>

				<div>
					<Text variant='large' weight='semibold' className='mb-4'>
						Pre-Launch Checklist
					</Text>
					<div className='space-y-2'>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${pecCycle?.name ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Cycle name configured</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${pecCycle?.populationId ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Population selected</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${hasEnoughParticipants ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>
								At least 2 participants
								{populationCount !== undefined && ` (${populationCount} selected)`}
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${pecCycle?.promptText ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Prompt text configured</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${pecCycle?.emailTemplateId ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Email template selected (optional)</Text>
						</div>
					</div>
				</div>

				<div className='space-y-4'>
					{pecCycle?.status === 'draft' && (
						<Button variant='primary' onClick={handleMarkReady} disabled={isPending}>
							Mark as Ready
						</Button>
					)}
					{pecCycle?.status === 'ready' && (
						<Button variant='primary' onClick={handleLaunch} disabled={isPending}>
							Launch Cycle
						</Button>
					)}
					{pecCycle?.status === 'active' && (
						<div>
							<Text variant='small' className='text-green-700 mb-4'>
								This cycle is currently active. Participants can submit their responses.
							</Text>
							<Button variant='destructive' onClick={handleClose} disabled={isPending}>
								Close Cycle
							</Button>
						</div>
					)}
					{pecCycle?.status === 'closed' && (
						<Text variant='small' className='text-neutral-600'>
							This cycle has been closed. View results in the Results tab.
						</Text>
					)}
				</div>

				{/* Response Status - shown when cycle is active or closed */}
				{statistics && (pecCycle?.status === 'active' || pecCycle?.status === 'closed') && (
					<div>
						<Text variant='large' weight='semibold' className='mb-4'>
							Response Status
						</Text>
						<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
							<div className='bg-gray-50 border border-gray-200 rounded-lg p-4 text-center'>
								<Text variant='xsmall' className='text-neutral-500'>
									Participants
								</Text>
								<Text variant='large' weight='semibold' className='mt-1'>
									{statistics.participantCount}
								</Text>
							</div>
							<div className='bg-green-50 border border-green-200 rounded-lg p-4 text-center'>
								<Text variant='xsmall' className='text-green-700'>
									Submitted
								</Text>
								<Text variant='large' weight='semibold' className='mt-1 text-green-700'>
									{statistics.submittedParticipants}
								</Text>
							</div>
							<div className='bg-amber-50 border border-amber-200 rounded-lg p-4 text-center'>
								<Text variant='xsmall' className='text-amber-700'>
									Not Submitted
								</Text>
								<Text variant='large' weight='semibold' className='mt-1 text-amber-700'>
									{statistics.notSubmittedParticipants}
								</Text>
							</div>
						</div>

						{/* Progress bar */}
						<div className='mt-4'>
							<div className='flex justify-between mb-1'>
								<Text variant='xsmall' className='text-neutral-600'>
									Completion Rate
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
									{Math.round(statistics.completionRate)}%
								</Text>
							</div>
							<div className='w-full bg-gray-200 rounded-full h-2'>
								<div
									className='bg-green-500 h-2 rounded-full transition-all'
									style={{ width: `${Math.min(statistics.completionRate, 100)}%` }}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
